import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, TrendingDown, UserX, XCircle } from 'lucide-react';
import api from '../../../utils/api';
import { isoDate } from '../../../utils/time';
import {
  PAGE_WIDE, PageHeader, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton, num, fmtDate, plural,
} from '../../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, TFOOT_ROW, MoneyCell, Notice,
  DateRange, RefreshButton, codeLabel,
} from '../financeKit';

// The report lists at most 50 shifts per check.
const LIST_CAP = 50;

const day = (v) => fmtDate(v ? String(v).slice(0, 10) : '');
const sumOf = (rows, key) => rows.reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);
const countLabel = (c) => (c >= LIST_CAP ? `${num(LIST_CAP)}+` : num(c));
const marginText = (profit, revenue) => (Number(revenue) ? `${num((Number(profit) / Number(revenue)) * 100, 1)}%` : '—');

function MarginCell({ value }) {
  const v = Number(value || 0);
  return <span className={v < 0 ? 'text-red-600' : 'text-slate-900'}>{num(v, 1)}%</span>;
}

// Columns shared by the two missing-rate lists.
const RATE_COLUMNS = [
  { key: 'shift_date',  label: 'Date',       render: r => day(r.shift_date), className: 'whitespace-nowrap' },
  { key: 'staff_name',  label: 'Staff',      render: r => <span className="font-medium text-slate-800">{r.staff_name || '—'}</span> },
  { key: 'client_name', label: 'Client',     render: r => r.client_name || '—' },
  { key: 'shift_type',  label: 'Shift type', render: r => <span title={r.shift_type || ''}>{codeLabel(r.shift_type) || '—'}</span> },
  { key: 'hours',       label: 'Hours', num: true, render: r => num(r.hours, 1), total: rows => num(sumOf(rows, 'hours'), 1) },
];

// One check: a table of the shifts it found, with a totals row, or a short all-clear.
function AuditSection({ title, icon: Icon, tone = 'red', rows, columns, empty, capNote }) {
  const hasTotals = columns.some(c => c.total);
  const badge = rows.length === 0
    ? <span className="badge-green">None</span>
    : <span className={tone === 'amber' ? 'badge-yellow' : 'badge-red'}>{rows.length >= LIST_CAP ? `${num(LIST_CAP)}+ shifts` : plural(rows.length, 'shift')}</span>;
  return (
    <SectionCard title={title} icon={Icon} actions={badge}>
      {rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} title={empty.title} hint={empty.hint} />
      ) : (
        <>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr className={THEAD_ROW}>
                  {columns.map(c => (
                    <th key={c.key} scope="col" className={c.num ? TH_NUM : TH}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={row.id || i} className={TR}>
                    {columns.map(c => (
                      <td key={c.key} className={`${c.num ? TD_NUM : TD} ${c.className || ''}`}>
                        {c.render ? c.render(row) : row[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {hasTotals && (
                <tfoot>
                  <tr className={TFOOT_ROW}>
                    {columns.map((c, i) => (
                      <td key={c.key} className={c.num ? TD_NUM : TD}>{i === 0 ? 'Total' : (c.total ? c.total(rows) : null)}</td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {rows.length >= LIST_CAP && (
            <p className="px-4 py-2.5 text-xs text-slate-500 border-t border-slate-100">{capNote}</p>
          )}
        </>
      )}
    </SectionCard>
  );
}

export default function AuditReport() {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [from, setFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 1);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(today));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/financials/reports/audit?from=${from}&to=${to}`);
      setData(r.data || null);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.message || "We couldn't load the rates audit. Check your connection, then try again.");
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totalIssues = data
    ? (data.counts?.missing_client || 0) + (data.counts?.missing_staff || 0) +
      (data.counts?.zero_profit || 0) + (data.counts?.below_margin || 0)
    : 0;

  const c = data?.counts || {};
  const target = data?.target_margin_pct || 0;
  const ready = !loading && !!data;

  return (
    <div className={PAGE_WIDE}>
      <PageHeader
        title="Rates audit"
        description="Shifts in the range with a missing client or staff rate, no profit, or a margin below target."
        actions={(
          <>
            <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="audit" />
            <RefreshButton onClick={load} loading={loading} label="Refresh audit" />
          </>
        )}
      />

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Issue counts — rose only when there is something to fix. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Missing client rate" value={ready ? countLabel(c.missing_client || 0) : '…'}
              accent="primary" alert={ready && (c.missing_client || 0) > 0} sub={ready ? 'Shifts to fix' : null} />
            <KpiTile label="Missing staff pay rate" value={ready ? countLabel(c.missing_staff || 0) : '…'}
              accent="slate" alert={ready && (c.missing_staff || 0) > 0} sub={ready ? 'Shifts to fix' : null} />
            <KpiTile label="Zero or negative profit" value={ready ? countLabel(c.zero_profit || 0) : '…'}
              accent="slate" alert={ready && (c.zero_profit || 0) > 0} sub={ready ? 'Shifts to review' : null} />
            <KpiTile label="Below target margin" value={ready ? countLabel(c.below_margin || 0) : '…'}
              accent="slate" sub={ready ? `Target ${Number(target)}%` : null} />
          </div>

          {/* Health line */}
          {ready && (totalIssues === 0 ? (
            <div role="status" className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
              <CheckCircle2 size={16} className="shrink-0 text-emerald-600" aria-hidden="true" />
              <p className="text-sm font-medium text-emerald-800">
                No issues found in this range. <span className="font-normal text-emerald-700">Target margin {Number(target)}%.</span>
              </p>
            </div>
          ) : (
            <Notice tone="amber" title={`${plural(totalIssues, 'issue')} ${totalIssues === 1 ? 'needs' : 'need'} attention`}>
              Fix missing rates first: without them a shift's charge or pay can't be worked out. Target margin {Number(target)}%.
            </Notice>
          ))}

          {loading ? (
            <div className="card"><TableSkeleton rows={6} cols={5} /></div>
          ) : !data ? null : (
            <div className="space-y-6">
              <AuditSection
                title="Missing client rate"
                icon={XCircle}
                rows={data.missing_client_rate || []}
                empty={{ title: 'Every shift has a client rate', hint: 'Nothing to fix for this range.' }}
                capNote="Showing the 50 most recent. Fix these and refresh to see the rest."
                columns={RATE_COLUMNS}
              />

              <AuditSection
                title="Missing staff pay rate"
                icon={UserX}
                rows={data.missing_staff_rate || []}
                empty={{ title: 'Every shift has a staff pay rate', hint: 'Nothing to fix for this range.' }}
                capNote="Showing the 50 most recent. Fix these and refresh to see the rest."
                columns={RATE_COLUMNS}
              />

              <AuditSection
                title="Zero or negative profit"
                icon={AlertTriangle}
                rows={data.zero_profit || []}
                empty={{ title: 'No shifts at zero or negative profit', hint: 'Every charged shift in this range made money.' }}
                capNote="Showing the 50 most recent."
                columns={[
                  { key: 'shift_date',  label: 'Date',   render: r => day(r.shift_date), className: 'whitespace-nowrap' },
                  { key: 'staff_name',  label: 'Staff',  render: r => <span className="font-medium text-slate-800">{r.staff_name || '—'}</span> },
                  { key: 'client_name', label: 'Client', render: r => r.client_name || '—' },
                  { key: 'revenue',     label: 'Revenue',    num: true, render: r => <MoneyCell value={r.revenue} />,    total: rows => <MoneyCell value={sumOf(rows, 'revenue')} strong /> },
                  { key: 'staff_cost',  label: 'Staff cost', num: true, render: r => <MoneyCell value={r.staff_cost} />, total: rows => <MoneyCell value={sumOf(rows, 'staff_cost')} strong /> },
                  { key: 'net_profit',  label: 'Profit',     num: true, render: r => <MoneyCell value={r.net_profit} />, total: rows => <MoneyCell value={sumOf(rows, 'net_profit')} strong /> },
                ]}
              />

              <AuditSection
                title={`Below the ${Number(target)}% target margin`}
                icon={TrendingDown}
                tone="amber"
                rows={data.below_margin || []}
                empty={{ title: 'Every shift meets the target margin', hint: 'Nothing below target for this range.' }}
                capNote="Showing the 50 lowest margins."
                columns={[
                  { key: 'shift_date',  label: 'Date',   render: r => day(r.shift_date), className: 'whitespace-nowrap' },
                  { key: 'staff_name',  label: 'Staff',  render: r => <span className="font-medium text-slate-800">{r.staff_name || '—'}</span> },
                  { key: 'client_name', label: 'Client', render: r => r.client_name || '—' },
                  { key: 'revenue',     label: 'Revenue', num: true, render: r => <MoneyCell value={r.revenue} />,    total: rows => <MoneyCell value={sumOf(rows, 'revenue')} strong /> },
                  { key: 'net_profit',  label: 'Profit',  num: true, render: r => <MoneyCell value={r.net_profit} />, total: rows => <MoneyCell value={sumOf(rows, 'net_profit')} strong /> },
                  { key: 'margin_pct',  label: 'Margin',  num: true, render: r => <MarginCell value={r.margin_pct} />,
                    total: rows => marginText(sumOf(rows, 'net_profit'), sumOf(rows, 'revenue')) },
                ]}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
